import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { SHARES, BUSINESS, fmtSteers } from "@/lib/beef";
import { Loader2, CheckCircle2 } from "lucide-react";
import QRCode from "react-qr-code";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Reserve = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", share: "half", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [prices, setPrices] = useState(null);

  const soldOut = availability !== null && availability.remaining <= 0;

  useEffect(() => {
    axios.get(`${API}/availability`).then(({ data }) => setAvailability(data)).catch(() => {});
    axios.get(`${API}/pricing`).then(({ data }) => setPrices(data)).catch(() => {});
  }, []);

  const depositFor = (id) => {
    const d = prices?.[`deposit_${id}`];
    const v = d !== undefined ? Number(d) : SHARES.find((s) => s.id === id).deposit;
    return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
  };

  const share = SHARES.find((s) => s.id === form.share);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitReservation = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please add your name, email, and phone number — we call or text every customer.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}${soldOut ? "/waitlist" : "/reservations"}`, form);
      setReservation(data);
      toast.success(soldOut ? "You're on the waitlist — we'll call you when a steer is ready." : "Reservation received — we'll be in touch within 48 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-linen bg-cream px-4 py-3.5 text-base text-forest placeholder:text-forest/40 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest/40 transition-[box-shadow,border-color] duration-200";

  return (
    <section id="reserve" className="py-24 lg:py-32 bg-forest text-cream" data-testid="reserve-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-clay mb-4">Reserve Your Beef</p>
          <p className="text-cream/70 text-base leading-relaxed max-w-md">
            Tell us what you'd like and we'll confirm your animal and harvest date within 48 hours.
            A deposit is required to reserve your steer, and all butcher fees are included in
            your price.
          </p>
          <div className="mt-10 space-y-3 text-sm text-cream/70">
            <p data-testid="reserve-contact-phone">Call {BUSINESS.contactName}: <a href={BUSINESS.phoneHref} className="text-cream font-medium underline underline-offset-4">{BUSINESS.phone}</a></p>
            <p data-testid="reserve-contact-email">Email: <span className="text-cream font-medium">{BUSINESS.email}</span></p>
          </div>
          {availability && (
            <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-cream/25 px-5 py-2.5" data-testid="availability-pill">
              <span className={`w-2 h-2 rounded-full ${soldOut ? "bg-clay" : "bg-green-400"}`} />
              <span className="text-cream text-sm font-semibold">
                {soldOut
                  ? "Sold out this season — join the waitlist and we'll call you first"
                  : `Only ${fmtSteers(availability.remaining)} ${availability.remaining === 1 ? "steer" : "steers"} left this season`}
              </span>
            </div>
          )}
          <div className="mt-10 flex items-center gap-5" data-testid="qr-share-block">
            <div className="bg-cream rounded-xl p-3 shadow-lg">
              <QRCode value="https://watersandfield.com" size={110} fgColor="#1A3626" />
            </div>
            <p className="text-cream/70 text-sm leading-relaxed max-w-[180px]">
              Scan to pull this page up on any phone — easy to share with family splitting a share.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          {!reservation ? (
            <form onSubmit={submitReservation} className="bg-white text-forest rounded-2xl p-8 sm:p-10 space-y-6" data-testid="reserve-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-moss block mb-2">Name</label>
                  <input data-testid="reserve-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Cattleman" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-moss block mb-2">Email</label>
                  <input data-testid="reserve-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@example.com" />
                </div>
              </div>
              <div>
                <label className="text-xs tracking-[0.15em] uppercase font-bold text-moss block mb-2">Phone</label>
                <input data-testid="reserve-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(904) 555-0000" required />
              </div>
              <div>
                <label className="text-xs tracking-[0.15em] uppercase font-bold text-moss block mb-3">Choose your share</label>
                <div className="grid grid-cols-3 gap-3">
                  {SHARES.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`reserve-share-${s.id}`}
                      onClick={() => setForm((f) => ({ ...f, share: s.id }))}
                      className={`rounded-xl border px-4 py-4 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${
                        form.share === s.id ? "border-clay bg-clay/5 ring-1 ring-clay" : "border-linen bg-cream hover:border-moss/40"
                      }`}
                    >
                      <span className="font-display text-lg font-medium block">{s.name}</span>
                      <span className="text-xs text-moss block mt-1">{soldOut ? "Join waitlist" : `$${depositFor(s.id)} deposit`}</span>
                    </button>
                  ))}
                </div>
              </div>
              {form.share !== "quarter" && (
                <div className="rounded-xl border border-clay/30 bg-clay/5 px-5 py-4" data-testid="cut-sheet-note">
                  <p className="text-sm text-forest/85 leading-relaxed">
                    <span className="font-semibold">Half &amp; whole orders:</span> once you reserve, we'll go over
                    your cut sheet together with the butcher — every cut, your way.
                  </p>
                </div>
              )}
              <div>
                <label className="text-xs tracking-[0.15em] uppercase font-bold text-moss block mb-2">Notes (optional)</label>
                <textarea
                  data-testid="reserve-notes-input"
                  rows={3}
                  className={inputCls}
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Timing, cut preferences, delivery questions…"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="reserve-submit-btn"
                className="w-full rounded-full bg-clay text-cream font-semibold px-8 py-4 text-base hover:bg-claydark hover:-translate-y-0.5 hover:shadow-lg transition-[background-color,transform,box-shadow] duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {soldOut ? "Hold my slot — notify me" : `Reserve my ${share.name.toLowerCase()}`}
              </button>
            </form>
          ) : (
            <div className="bg-white text-forest rounded-2xl p-8 sm:p-10" data-testid="reservation-success">
              <CheckCircle2 className="w-10 h-10 text-clay" strokeWidth={1.5} />
              <h3 className="font-display text-2xl sm:text-3xl tracking-tight font-medium mt-6">
                You're on the list, {reservation.name.split(" ")[0]}.
              </h3>
              <p className="text-moss text-base leading-relaxed mt-4">
                Your <span className="font-semibold text-forest">{reservation.share}</span> share {soldOut ? "waitlist spot" : "reservation"} is saved.
                We'll email <span className="font-semibold text-forest">{reservation.email}</span> {soldOut
                  ? "as soon as a steer becomes available — your slot is held."
                  : "within 48 hours with your harvest window."}
              </p>
              {!soldOut && (
              <div className="mt-8 rounded-xl border border-linen bg-cream p-6" data-testid="deposit-instructions">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-moss">What happens next</p>
                <p className="text-sm text-forest/80 leading-relaxed mt-3">
                  We'll reach out by call or text within 48 hours with payment options for your
                  ${depositFor(share.id)} deposit. Deposits are required and all sales are final — no refunds.
                  Your animal is set aside and finished just for you.
                </p>
                <p className="text-xs text-moss mt-3 pt-3 border-t border-linen">
                  <span className="font-semibold text-forest">One thing to plan for:</span> bring your own large coolers or insulated bags
                  to the meetup — a share can be several hundred pounds of beef, and our transport coolers stay with us.
                </p>
              </div>
              )}
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
};
